<script lang="ts" setup>
import { computed, ref } from "vue";
import type { FormElementBase } from "~/api";
import {
  defaultFormComponents,
  type FormElementDefault,
  structureElements,
  contentElements,
  actionElements,
  housingElements,
} from "../FormComponents/defaultComponents";

const props = withDefaults(
  defineProps<{
    /** Components to display in the palette as draggable. */
    components?: FormElementDefault<FormElementBase>[];
    /** Whether the palette is in read-only mode (non-draggable) */
    readOnly?: boolean;
  }>(),
  {
    components: () => defaultFormComponents,
    readonly: false,
  },
);
// providing a default value
const isDragging = defineModel<boolean>("isDragging");

const emit = defineEmits<{
  (e: "component", payload: FormElementDefault<FormElementBase>): void;
}>();

const elementSearch = ref("");
const questionElements = computed<FormElementDefault<FormElementBase>[]>(() => {
  const search = elementSearch.value.toLowerCase();
  const list = props.components
    .filter((c) => c.label?.toLowerCase().includes(search))
    .filter(Boolean);
  // sort by label
  list.sort((a, b) =>
    (a.label ?? a.questionCode).localeCompare(b.label ?? b.questionCode),
  );
  return list;
});

const handleOnDrag = (
  e: DragEvent,
  component: FormElementDefault<FormElementBase>,
) => {
  // To fix chrome bug. See:
  // https://stackoverflow.com/questions/14203734/dragend-dragenter-and-dragleave-firing-off-immediately-when-i-drag#20733870
  setTimeout(() => {
    isDragging.value = e.type === "dragstart";
  }, 10);

  if (e.dataTransfer) {
    if (e.type === "dragstart") {
      // can't use dataTransfer.setData() because the tinyMCE editor will accept it and we don't want to drop there
      emit("component", component);
      e.dataTransfer.effectAllowed = "copy";
    }
  }
};
</script>

<template id="cpldk-form-designer-actions">
  <div class="palette">
    <!-- <div class="finder-header">
        <span class="finder-title">
          <el-tooltip content="" placement="left" :show-after="1000">
            <template #content>
              The listing of available form elements<br />
              Search for elements by their name<br />
              Drag and drop elements into your form to add them
            </template>
            <el-icon>
              <el-icon-info-filled />
            </el-icon>
          </el-tooltip>
        </span>
        <el-input
          v-model="elementSearch"
          size="small"
          clearable
          class="finder-search"
          placeholder="Search..."
        ></el-input>
      </div> -->
    <div class="designerPalette">
      <div class="designerPalette-group">
        <div class="designerPalette-group-name">Inputs</div>
        <div class="designerPalette-item-group">
          <div
            v-for="component in questionElements"
            :key="component.questionCode"
            class="designerPalette-item"
            :class="{ 'readonly-item': props.readOnly }"
            :draggable="!props.readOnly"
            @dragstart="!props.readOnly && handleOnDrag($event, component)"
            @dragend="!props.readOnly && handleOnDrag($event, component)"
          >
            <div class="designerPalette-item-name">
              <i :class="`mdi mdi-${component.icon}`"></i>
              {{ component.label }}
              <!-- {{ component.properties }} -->
            </div>
          </div>
        </div>

        <div class="designerPalette-group-name">Actions</div>
        <div class="designerPalette-item-group">
          <div
            v-for="component in actionElements"
            :key="component.questionCode"
            class="designerPalette-item"
            :class="{ 'readonly-item': props.readOnly }"
            :draggable="!props.readOnly"
            @dragstart="!props.readOnly && handleOnDrag($event, component)"
            @dragend="!props.readOnly && handleOnDrag($event, component)"
          >
            <div class="designerPalette-item-name">
              <i :class="`mdi mdi-${component.icon}`"></i>
              {{ component.label }}
              <!-- {{ component.properties }} -->
            </div>
          </div>
        </div>

        <div class="designerPalette-group-name">Text Content</div>
        <div class="designerPalette-item-group">
          <div
            v-for="component in contentElements"
            :key="component.questionCode"
            class="designerPalette-item"
            :class="{ 'readonly-item': props.readOnly }"
            :draggable="!props.readOnly"
            @dragstart="!props.readOnly && handleOnDrag($event, component)"
            @dragend="!props.readOnly && handleOnDrag($event, component)"
          >
            <div class="designerPalette-item-name">
              <i :class="`mdi mdi-${component.icon}`"></i>
              {{ component.label }}
              <!-- {{ component.properties }} -->
            </div>
          </div>
        </div>

        <div class="designerPalette-group-name">Structure & Other</div>
        <div class="designerPalette-item-group">
          <div
            v-for="component in structureElements"
            :key="component.questionCode"
            class="designerPalette-item"
            :class="{ 'readonly-item': props.readOnly }"
            :draggable="!props.readOnly"
            @dragstart="!props.readOnly && handleOnDrag($event, component)"
            @dragend="!props.readOnly && handleOnDrag($event, component)"
          >
            <div class="designerPalette-item-name">
              <i :class="`mdi mdi-${component.icon}`"></i>
              {{ component.label }}
              <!-- {{ component.properties }} -->
            </div>
          </div>
        </div>

        <div class="designerPalette-group-name">Housing Module</div>
        <div class="designerPalette-item-group">
          <div
            v-for="component in housingElements"
            :key="component.questionCode"
            class="designerPalette-item"
            :class="{ 'readonly-item': props.readOnly }"
            :draggable="!props.readOnly"
            @dragstart="!props.readOnly && handleOnDrag($event, component)"
            @dragend="!props.readOnly && handleOnDrag($event, component)"
          >
            <div class="designerPalette-item-name">
              <i :class="`mdi mdi-${component.icon}`"></i>
              {{ component.label }}
              <!-- {{ component.properties }} -->
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
.designerPalette {
  padding: 10px 0px;
  overflow: auto;
  h2 {
    font-size: large;
  }

  .designerPalette-group {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    gap: 3px;

    overflow: auto;
    padding: 0px 15px 15px 20px;

    .designerPalette-group-name {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 3px;
      width: 100%;
      color: #757575;
      font-weight: normal;
      //border-bottom: 2px solid #ccc;
    }

    .designerPalette-group-none {
      width: 100%;
      text-align: center;
      color: #ccc;
      margin-top: 5px;
    }

    .designerPalette-item-group {
      padding: 0;
      gap: 3px 10px;
      column-count: 3;
      margin: 0 0 20px 15px;
    }

    .designerPalette-item {
      border-radius: 5px;
      border: 1px solid #ccc;
      box-sizing: border-box;
      padding: 1px 6px;
      margin: 2px 0;
      user-select: none;
      cursor: pointer;

      &:hover {
        border-color: var(--el-color-primary);
      }
    }
    .designerPalette-item-name {
      i {
        color: var(--el-color-primary);
      }
    }

    .used-compliance-item {
      background-color: rgb(242, 236, 236);
      pointer-events: none;
    }

    .readonly-item {
      background-color: #f5f5f5;
      cursor: default !important;
      opacity: 0.8;

      &:hover {
        border-color: #ccc;
      }
    }
  }
}
</style>
